import { Component, OnInit } from '@angular/core';
import { JeevesProductGridViewComponent } from 'gung-standard-jeeves';
import {
  AuthService,
  Availability,
  CustomerProductPrice,
  GridViewProductCard,
  GungAnonymousConfigService,
  GungFlowService,
  LocationConfigService,
  NavbarConfigService,
  PriceService,
  Product,
  SelectedCustomerService
} from 'gung-standard';
import { Router } from '@angular/router';
import { first, forkJoin, of, switchMap, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { TyloPriceConfigService } from '../../services/tylo-price-config.service';
import { TyloPriceMarkup } from '../tylo-customer-b2c-price-markup-modal/tylo-customer-b2c-price-markup-modal.component';

@Component({
    selector: 'tylo-product-grid-view',
    templateUrl: './tylo-product-grid-view.component.html',
    styleUrls: ['./tylo-product-grid-view.component.css'],
    standalone: false
})
export class TyloProductGridViewComponent extends JeevesProductGridViewComponent implements OnInit {
  isOnlyDisplay = false;
  currentLang = this.translateService.currentLang || 'se';

  customerPriceMarkup: TyloPriceMarkup;
  showB2CPrices: boolean = false;
  hidePrice: boolean = false;

  constructor(
    protected priceService: PriceService,
    protected gungFlowService: GungFlowService,
    protected authService: AuthService,
    protected router: Router,
    protected locationConfigService: LocationConfigService,
    protected navbarConfigService: NavbarConfigService,
    protected translateService: TranslateService,
    protected gungAnonymousConfigService: GungAnonymousConfigService,
    protected selectedCustomerService: SelectedCustomerService,
    protected priceConfig: TyloPriceConfigService
  ) {
    super(priceService, gungFlowService, authService, gungAnonymousConfigService, router, locationConfigService);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.navbarConfigService
      .showCart()
      .pipe(first())
      .subscribe(showCart => (this.isOnlyDisplay = !showCart));

    this.priceConfig.showB2CPrices
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(showB2CPrices => (this.showB2CPrices = showB2CPrices));

    this.priceConfig.hidePrice
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(hidePrice => (this.hidePrice = hidePrice));
  }

  // Ensure that we navigate to the correct product detail page, in standard we navigate to /concept/:id for concepts, but for Tylo we navigate to /product/:id
  goToDetail(product: Product): void {
    const productId = this.getProductIdToNavigateToDetail(product);

    this.router.navigate(['/product/', productId]);
  }

  protected subscribeToProducts() {
    if (!this.oldFlow || !this.currentFlow || this.oldFlow.id !== this.currentFlow.id) {
      // remove cached data;
      this.keyedMapData = {};
      this.oldFlow = this.currentFlow;
    }
    const newIds = this.data.map(p => p.id).filter(id => !Object.keys(this.keyedMapData).includes(id));
    if (newIds.length === 0) {
      this.readMappedDataFromCache();
      return;
    }

    const subscription = forkJoin([
      of(this.data),
      this.priceService.getCurrentCustomerPrices(newIds).pipe(first()),
      this.gungFlowService.getSelectedFlow().pipe(first()),
      this.selectedCustomerService.getSelectedCustomer().pipe(
        first(),
        switchMap(selectedCustomer => this.priceConfig.getB2CPriceMarkup(selectedCustomer.id).pipe(first()))
      )
    ])
      .pipe(first())
      .subscribe(([productData, prices, flow, customerPriceMarkup]) => {
        this.customerPriceMarkup = customerPriceMarkup;
        let avs = [];
        if (this.isAnonymous) {
          this.includeAvailability =
            this.gungAnonymousConfigService.showAnonymousAvailabilities &&
            (flow.useAvailabilities || flow.requireAvailability);
        } else {
          this.includeAvailability = flow.useAvailabilities || flow.requireAvailability;
        }
        if (this.includeAvailability) {
          avs = newIds.map(id => this.getProductAvailability(this.data.find(product => product.id === id)));
        }

        this.keyedMapData = {
          ...this.keyedMapData,
          ...newIds.reduce((acc, curr) => {
            const product = productData.filter(p => p && p.id === curr)[0];

            if (!product) {
              this.findError = true;
              this.errorMessage += 'No product found. ';
              throw new Error('No product found');
            }

            const price = prices.filter(p => p && p.productId === curr)[0];
            if (!price) {
              this.findError = true;
              this.errorMessage += 'No price found.';
              throw new Error('No price found');
            }

            const av = avs.filter(p => p && p.productId === curr)[0];
            if (!av && this.includeAvailability) {
              this.findError = true;
              this.errorMessage += 'No availability found. ';
              throw new Error('No availability found');
            }
            const item: GridViewProductCard = this.mapItem(curr, product, price, av);
            return {
              ...acc,
              [curr]: item
            };
          }, {})
        };
        this.readMappedDataFromCache();
      });
    this.subscriptions.push(subscription);
  }

  protected mapItem(
    id: string,
    product: Product,
    price: CustomerProductPrice,
    availability: Availability
  ): GridViewProductCard {
    if (!!this.customerPriceMarkup) {
      if (!price.extra) {
        price.extra = {};
      }
      price.extra.b2cPrice = this.priceConfig.calculatePriceMarkup(
        this.customerPriceMarkup,
        price.customerGrossPrice?.value || 0
      );
    }
    return {
      product,
      price,
      availability
    };
  }
}
